import { call, delay, put, race, select, take } from 'redux-saga/effects';

import * as D from '../../deps';

const log = D.Logger.child({ component: 'sagas/workers/Application' });

const LOOP = true;

export function* pausePersistor(persistor) {
  yield call(persistor.pause);
  yield put(D.AppActions.setIsPersistingState(false));
  log.trace('Pausing persistance');
}

/**
 * Watches for logout attempts and makes sure that the persistor is resumed so changes
 * in login state get persisted before the app reloads to the sign-in page. This is
 * necessary in addition to the managePersistor watcher above because it's possible that
 * it can delay the loop for up to 2 seconds before checking state, which might prevent
 * the logout state changes from persisting.
 */
export function* onLogoutAttempt() {
  const isPersisting = yield select(D.AppUtils.getIsPersistingDataLocally);
  const persistor = yield call(D.getPersistor);
  if (!isPersisting && persistor) {
    yield call(resumePersistor, persistor);
  }
}

export function* resumePersistor(persistor) {
  yield call(persistor.resume);
  yield put(D.AppActions.setIsPersistingState(true));
  log.trace('Resuming persistance');
}

/**
 * Watcher that pauses and resumes the writing of our store to local storage.
 * This assumes that if we sample the same action after 100ms that we should pause
 * the writing of local storage. We will continue sampling and if we see events that
 * are not the same we will resume persisting on every store change. There is an opportunity
 * to force pause the persistor for a long pause if Constants.AppActions.AppActions.NOTIFY_CRITICAL_RENDER_PATH
 * action type is seen.
 */
export function* managePersistor() {
  let prevAction;
  let currentAction;
  let persistor;
  const LONG_PAUSE = 2000;
  const SHORT_PAUSE = 100;
  // in order to not slow down time to music we only persist after music starts playing
  // In order to fix https://jira.savagebeast.com/browse/WEB-9465 for now this line was taking out
  // so we start persisting the store right away
  // yield take(Constants.AudioPlayer.AudioPlayerActions.PLAYING);
  while (LOOP) {
    currentAction = yield take('*');
    persistor = persistor || D.getPersistor();
    if (!persistor) continue;
    const isLoggingOut = yield select(D.AppUtils.getIsLoggingOut);
    const isPersisting = yield select(D.AppUtils.getIsPersistingDataLocally);
    const forcePause = currentAction.type === D.AppConstants.AppActions.NOTIFY_CRITICAL_RENDER_PATH;
    if (prevAction) {
      if (!isLoggingOut && (prevAction.type === currentAction.type || forcePause)) {
        if (isPersisting) {
          yield call(pausePersistor, persistor);
        }
      } else {
        if (!isPersisting) {
          yield call(resumePersistor, persistor);
        }
      }
    }
    prevAction = currentAction;
    // We just do this race so we can give the critical render path action
    // an opportunity to pause persistor any time it is fired
    const raceResult = yield race({
      delay: delay(forcePause ? LONG_PAUSE : SHORT_PAUSE),
      forcePause: take(D.AppConstants.AppActions.NOTIFY_CRITICAL_RENDER_PATH),
    });

    if (raceResult.forcePause) {
      yield call(pausePersistor, persistor);
      yield delay(LONG_PAUSE);
    }
  }
}
